import { ConditionOperatorEnumType, CountOperator } from '@reach/shared-types/conditions';
import { ChevronDown, ChevronRight, Trash2 } from 'lucide-react';
import React from 'react';
import type {
  FieldInfo,
  RelatedSchemaWithFieldTypes,
} from '../../engage-segments-tab/segment-definitions-api';
import { FieldConditionRow } from './FieldConditionRow';
import { RelatedResourceBlock } from './RelatedResourceBlock';
import type {
  BuilderCondition,
  BuilderFieldCondition,
  BuilderNestedGroup,
  BuilderRelatedResourceCondition,
} from './segment-builder.types';
import {
  isBuilderFieldCondition,
  isBuilderNestedGroup,
  isBuilderRelatedResourceCondition,
} from './segment-builder.types';
import styles from './SegmentVisualBuilder.module.css';

function generateId(): string {
  return crypto.randomUUID();
}

interface NestedGroupBlockProps {
  condition: BuilderNestedGroup;
  contactFields: FieldInfo[];
  relatedSchemas: RelatedSchemaWithFieldTypes[];
  onUpdate: (condition: BuilderNestedGroup) => void;
  onDelete: () => void;
  disabled?: boolean;
  depth?: number;
}

/**
 * Recursive component for rendering nested condition groups.
 * Supports arbitrary nesting with field conditions, related resources, and other nested groups.
 */
export const NestedGroupBlock: React.FC<NestedGroupBlockProps> = ({
  condition,
  contactFields,
  relatedSchemas,
  onUpdate,
  onDelete,
  disabled = false,
  depth = 0,
}) => {
  const [addRelatedSchemaId, setAddRelatedSchemaId] = React.useState('');
  const isExpanded = condition.isExpanded;

  const toggleExpanded = () => {
    onUpdate({ ...condition, isExpanded: !condition.isExpanded });
  };

  const handleLogicChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const logic = e.target.value === 'or' ? 'or' : 'and';
    onUpdate({ ...condition, logic });
  };

  const handleConditionUpdate = (index: number) => (cond: BuilderCondition) => {
    const next = [...condition.conditions];
    next[index] = cond;
    onUpdate({ ...condition, conditions: next });
  };

  const handleConditionDelete = (index: number) => () => {
    const next = condition.conditions.filter((_, i) => i !== index);
    onUpdate({ ...condition, conditions: next });
  };

  const handleAddFieldCondition = () => {
    const firstField = contactFields[0];
    const newCond: BuilderFieldCondition = {
      id: generateId(),
      type: 'field',
      field: firstField?.name ?? '',
      fieldType: firstField?.type ?? 'unknown',
      operator: ConditionOperatorEnumType.EQUALS,
      value: [],
    };
    onUpdate({
      ...condition,
      conditions: [...condition.conditions, newCond],
    });
  };

  const handleAddNestedGroup = () => {
    // New nested group with opposite logic for clarity
    const newNestedGroup: BuilderNestedGroup = {
      id: generateId(),
      type: 'nested_group',
      logic: condition.logic === 'and' ? 'or' : 'and',
      conditions: [],
      isExpanded: true,
    };
    onUpdate({
      ...condition,
      conditions: [...condition.conditions, newNestedGroup],
    });
  };

  const handleAddRelatedResource = (schemaId: string) => {
    const schema = relatedSchemas.find(s => s.id === schemaId);
    if (!schema) return;

    const newCond: BuilderRelatedResourceCondition = {
      id: generateId(),
      type: 'related_resource',
      schemaId: schema.id,
      schemaName: schema.name,
      foreignKeyPath: schema.foreignKeyPath,
      countOperator: CountOperator.GTE,
      countValue: 1,
      conditions: [],
      isExpanded: true,
    };
    onUpdate({
      ...condition,
      conditions: [...condition.conditions, newCond],
    });
    setAddRelatedSchemaId('');
  };

  return (
    <div className={styles.nestedGroupBlock}>
      <div className={styles.nestedGroupHeader}>
        <div className={styles.nestedGroupTitle}>
          <button
            type="button"
            className={styles.relatedTitleButton}
            onClick={toggleExpanded}
            disabled={disabled}
            aria-expanded={isExpanded}
            aria-label={isExpanded ? 'Collapse' : 'Expand'}>
            {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          </button>
          <span>Nested Group</span>
          <select
            value={condition.logic}
            onChange={handleLogicChange}
            disabled={disabled}
            aria-label="Nested group logic">
            <option value="and">AND</option>
            <option value="or">OR</option>
          </select>
        </div>
        <button
          type="button"
          className={styles.relatedTitleButton}
          onClick={onDelete}
          disabled={disabled}
          aria-label="Remove nested group">
          <Trash2 size={16} />
        </button>
      </div>

      {isExpanded && (
        <div className={styles.nestedGroupContent}>
          {condition.conditions.map((cond, i) => (
            <React.Fragment key={cond.id}>
              {i > 0 && (
                <div className={styles.andConnector}>
                  <span className={styles.andConnectorLabel}>{condition.logic.toUpperCase()}</span>
                </div>
              )}
              {isBuilderFieldCondition(cond) ? (
                <div style={{ padding: '0 0.75rem' }}>
                  <FieldConditionRow
                    condition={cond}
                    fieldOptions={contactFields}
                    onUpdate={c => handleConditionUpdate(i)(c)}
                    onDelete={handleConditionDelete(i)}
                    disabled={disabled}
                  />
                </div>
              ) : isBuilderRelatedResourceCondition(cond) ? (
                <RelatedResourceBlock
                  condition={cond}
                  relatedSchemas={relatedSchemas}
                  onUpdate={c => handleConditionUpdate(i)(c)}
                  onDelete={handleConditionDelete(i)}
                  disabled={disabled}
                />
              ) : isBuilderNestedGroup(cond) ? (
                <div style={{ padding: '0 0.75rem' }}>
                  <NestedGroupBlock
                    condition={cond}
                    contactFields={contactFields}
                    relatedSchemas={relatedSchemas}
                    onUpdate={c => handleConditionUpdate(i)(c)}
                    onDelete={handleConditionDelete(i)}
                    disabled={disabled}
                    depth={depth + 1}
                  />
                </div>
              ) : null}
            </React.Fragment>
          ))}

          <div className={styles.nestedGroupAddRow}>
            <button
              type="button"
              className={styles.addConditionBtn}
              onClick={handleAddFieldCondition}
              disabled={disabled || contactFields.length === 0}>
              + Condition
            </button>
            <button
              type="button"
              className={styles.addConditionBtn}
              onClick={handleAddNestedGroup}
              disabled={disabled}>
              + Nested {condition.logic === 'and' ? 'OR' : 'AND'} group
            </button>
            <select
              className={styles.selectInput}
              value={addRelatedSchemaId}
              onChange={e => handleAddRelatedResource(e.target.value)}
              disabled={disabled || relatedSchemas.length === 0}
              style={{ width: 'auto', minWidth: '10rem' }}
              aria-label="Add related resource">
              <option value="">+ Related...</option>
              {relatedSchemas.map(s => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
};
